import { computePosition, flip, shift } from '@floating-ui/dom';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { Document } from '@tiptap/extension-document';
import { HardBreak } from '@tiptap/extension-hard-break';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Text } from '@tiptap/extension-text';
import { UndoRedo } from '@tiptap/extensions';
import {
  Editor,
  EditorContent,
  EditorContext,
  type JSONContent,
  posToDOMRect,
  ReactRenderer,
  useEditor,
  useEditorState
} from '@tiptap/react';
import { formatDuration, formatISO, intervalToDuration } from 'date-fns';
import { produce } from 'immer';
import {
  ArrowUpIcon,
  AtSignIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FilePlusIcon,
  GlobeIcon,
  icons,
  PaperclipIcon,
  RefreshCwIcon,
  SquareIcon,
  XIcon,
  ZapIcon
} from 'lucide-react';
import {
  type ComponentProps,
  type Dispatch,
  type DOMAttributes,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Markdown from 'react-markdown';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import ComboBoxField, {
  type Attributes as ComboBoxAttributes
} from './ComboBoxField';
import DateField, { type Attributes as DateFieldAttributes } from './DateField';
import Mention, { type Attributes as MentionAttributes } from './Mention';
import MentionList, {
  type MentionListHandle,
  type Props as MentionListProps
} from './MentionList';
import NumberField, {
  type Attributes as NumberFieldAttributes
} from './NumberField';
import SelectField, {
  type Attributes as SelectAttributes
} from './SelectField';
import Tag, { type TagAttributes } from './Tag';
import TextField, { type Attributes as TextFieldAttributes } from './TextField';
import textSerializers from './textSerializers';

import {
  type FileAttachment,
  FileAttachmentList
} from '@/components/FileAttachmentList';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

type Props =
  | {
      businessEntities: BusinessEntity[];
      handlePresetSelectRef: RefObject<((label: string) => void) | null>;
      initialPrompt?: JSONContent;
      messageId?: string;
      pending: boolean;
      presetPrompts: PresetPrompt[];
      sessionId: string;
      setIsEditing: Dispatch<SetStateAction<boolean>>;
      setPending: Dispatch<SetStateAction<boolean>>;
      setPromptsAndAnswers: Dispatch<SetStateAction<PromptAndAnswer[]>>;
      socket: null | WebSocket;
      state: 'editing';
      userId: string;
    }
  | {
      businessEntities: BusinessEntity[];
      handlePresetSelectRef: RefObject<((label: string) => void) | null>;
      initialPrompt?: JSONContent;
      messageId?: string;
      pending: boolean;
      presetPrompts: PresetPrompt[];
      sessionId: string;
      setPending: Dispatch<SetStateAction<boolean>>;
      setPromptsAndAnswers: Dispatch<SetStateAction<PromptAndAnswer[]>>;
      socket: null | WebSocket;
      state: 'prompting';
      userId: string;
    }
  | {
      handlePresetSelectRef: RefObject<((label: string) => void) | null>;
      initialPrompt?: JSONContent;
      state: 'read-only';
    };

const DropdownMenuItem = (
  props: ComponentProps<typeof DropdownMenuPrimitive.Item>
) => (
  <DropdownMenuPrimitive.Item
    className="focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
    data-slot="dropdown-menu-item"
    {...props}
  />
);

const DropdownMenuSub = ({ children }: { children: ReactNode }) => (
  <DropdownMenuPrimitive.Sub data-slot="dropdown-menu-sub">
    {children}
  </DropdownMenuPrimitive.Sub>
);

const DropdownMenuSubContent = ({ children }: { children: ReactNode }) => (
  <DropdownMenuPrimitive.SubContent
    className="bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-96 min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-hidden overflow-y-auto rounded-md border p-1 shadow-lg"
    data-slot="dropdown-menu-sub-content"
  >
    {children}
  </DropdownMenuPrimitive.SubContent>
);

const DropdownMenuSubTrigger = ({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) => (
  <DropdownMenuPrimitive.SubTrigger
    className={cn(
      'focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground flex cursor-default items-center rounded-sm px-2 py-1.5 text-sm outline-hidden select-none',
      className
    )}
    data-slot="dropdown-menu-sub-trigger"
  >
    {children}
  </DropdownMenuPrimitive.SubTrigger>
);

const Switch = ({
  className,
  ...props
}: ComponentProps<typeof SwitchPrimitive.Root>) => (
  <SwitchPrimitive.Root
    className={cn(
      'peer data-[state=checked]:bg-primary data-[state=unchecked]:bg-input focus-visible:border-ring focus-visible:ring-ring/50 dark:data-[state=unchecked]:bg-input/80 inline-flex h-[1.15rem] w-8 shrink-0 items-center rounded-full border border-transparent shadow-xs transition-all outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
      className
    )}
    data-slot="switch"
    {...props}
  >
    <SwitchPrimitive.Thumb
      className={cn(
        'bg-background pointer-events-none block size-4 rounded-full ring-0 transition-transform data-[state=checked]:translate-x-[calc(100%-2px)] data-[state=unchecked]:translate-x-0'
      )}
      data-slot="switch-thumb"
    />
  </SwitchPrimitive.Root>
);

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    tag: {
      insertTag: (text: string, color: string, iconName: string) => ReturnType;
    };
  }
}

const updatePosition = async (editor: Editor, element: HTMLElement) => {
  const virtualElement = {
    getBoundingClientRect: () =>
      posToDOMRect(
        editor.view,
        editor.state.selection.from,
        editor.state.selection.to
      )
  };

  const { strategy, x, y } = await computePosition(virtualElement, element, {
    middleware: [shift(), flip()],
    placement: 'bottom-start',
    strategy: 'absolute'
  });

  element.style.width = 'max-content';
  element.style.position = strategy;
  element.style.left = `${x.toString()}px`;
  element.style.top = `${y.toString()}px`;
};

const getSourceLogo = (sourceName: string): ReactNode => {
  switch (sourceName.toLowerCase()) {
    case 'backend':
    case 'opstream':
      return (
        <img
          alt="Opstream logo"
          src="/opstream-logo.svg"
          width={22}
        />
      );
    case 'ilevel':
      return (
        <img
          alt="iLevel logo"
          src="/ilevel-logo.svg"
          width={22}
        />
      );
    case 'reports':
      return (
        <img
          alt="PowerBI logo"
          className="mx-0.5"
          src="/powerbi-logo.svg"
          width={18}
        />
      );
    case 'web':
      return <GlobeIcon size={22} />;
    default:
      return <GlobeIcon size={22} />;
  }
};

const PromptField = ({
  handlePresetSelectRef,
  initialPrompt,
  ...remainingProps
}: Props) => {
  const { state } = remainingProps;
  const { getJWT } = useAuth();

  // Safely extract businessEntities as an array
  const businessEntities: BusinessEntity[] = useMemo(
    () =>
      state !== 'read-only' &&
      'businessEntities' in remainingProps &&
      Array.isArray(remainingProps.businessEntities)
        ? remainingProps.businessEntities
        : [],
    [state, remainingProps]
  );

  const dragDepthRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const [atDropdownMenuIsOpen, setAtDropdownMenuIsOpen] = useState(false);
  const [addDropdownIsOpen, setAddDropdownIsOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileAttachments, setFileAttachments] = useState<
    {
      error?: string;
      file: File;
      fileId?: string;

      id: string;

      progress?: number;

      status: 'blocked' | 'failed' | 'processing' | 'ready' | 'uploading';
    }[]
  >([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isLoadingSources, setIsLoadingSources] = useState(true);
  const [popoverIsOpen, setPopoverIsOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<null | PresetPrompt>(
    null
  );
  const [togglingSourceName, setTogglingSourceName] = useState<null | string>(
    null
  );
  const [userSources, setUserSources] = useState<SourceUserSchema[]>([]);
  const oauthPopupRef = useRef<null | Window>(null);
  const [isOAuthInProgress, setIsOAuthInProgress] = useState(false);
  const pendingOAuthSourceRef = useRef<null | string>(null);

  const sendMessage = useCallback(
    async (message: string, content: JSONContent) => {
      if (state === 'read-only') return;

      const messageId = uuidv4();

      remainingProps.setPromptsAndAnswers(promptsAndAnswers => [
        ...promptsAndAnswers,
        {
          answer: {
            blocks: [
              {
                data: { paragraph: 'Stopped.' },
                id: uuidv4(),
                type: 'paragraph'
              }
            ],
            id: uuidv4()
          },
          id: messageId,
          prompt: content,
          startTimestamp: '',
          tasks: [],
          thinkingDuration: null
        }
      ]);

      remainingProps.setPending(true);

      if (!remainingProps.socket) {
        throw new Error();
      }

      const authData = await getJWT();

      if (!authData) {
        remainingProps.setPending(false);
        return;
      }

      remainingProps.socket.send(
        JSON.stringify({
          ...(selectedPreset
            ? {
                content: {
                  prompt: selectedPreset.details,
                  user_message: message
                }
              }
            : {
                content: message
              }),
          data: {
            access_token: authData.accessToken,
            session_id: remainingProps.sessionId,
            user_id: authData.userId
          },
          message_id: messageId,
          type: 'query'
        })
      );
    },
    [remainingProps, selectedPreset, state, getJWT]
  );

  const editor = useEditor({
    autofocus: true,
    content: initialPrompt,
    editable: state === 'editing' || state === 'prompting',
    editorProps: {
      attributes: {
        'aria-label': 'Prompt field',
        class:
          state === 'editing' || state === 'prompting'
            ? 'max-h-52 overflow-y-auto p-4 outline-none'
            : '',
        name: 'prompt-field'
      },
      handleKeyDown(_, event) {
        if (state === 'read-only') return;

        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();

          if (atDropdownMenuIsOpen) return;

          if (submitButtonIsDisabled) return true;

          if (remainingProps.pending) return true;

          if (state === 'editing') {
            remainingProps.setIsEditing(false);
          }

          void sendMessage(
            editor.getText({ textSerializers }),
            editor.getJSON()
          );

          setTimeout(() => {
            editor.commands.clearContent();
          }, 0);

          if (state === 'prompting') {
            setTimeout(() => {
              window.scrollTo({
                behavior: 'smooth',
                top: document.body.scrollHeight
              });
            }, 100);
          }
        }
      }
    },
    extensions: [
      Document,
      Paragraph,
      Text,
      HardBreak,
      UndoRedo,
      Placeholder.configure({ placeholder: 'Ask anything' }),
      Tag,
      Mention.configure({
        deleteTriggerWithBackspace: true,
        HTMLAttributes: {
          class: 'tag'
        },
        suggestion: {
          allowSpaces: true,
          items: ({ query }) => {
            if (state === 'read-only') return [];

            return businessEntities
              .flatMap(businessEntity =>
                businessEntity.items.flatMap(item => {
                  if (item.items) {
                    return item.items.map(subItem => ({
                      businessEntityColor: businessEntity.color,
                      businessEntityIconName: businessEntity.iconName,
                      businessEntityId: businessEntity.id,
                      id: `${item.id}-${subItem.id}`,
                      name: `${item.name}: ${subItem.name}`
                    }));
                  } else {
                    return {
                      ...item,
                      businessEntityColor: businessEntity.color,
                      businessEntityIconName: businessEntity.iconName,
                      businessEntityId: businessEntity.id
                    };
                  }
                })
              )
              .filter(item =>
                item.name.toLowerCase().includes(query.toLowerCase())
              );
          },
          render: () => {
            let reactRenderer: ReactRenderer<
              MentionListHandle,
              MentionListProps
            >;

            return {
              onExit() {
                setAtDropdownMenuIsOpen(false);
                reactRenderer.destroy();
                reactRenderer.element.remove();
              },
              onKeyDown(props) {
                if (props.event.key === 'Escape') {
                  reactRenderer.destroy();
                  reactRenderer.element.remove();

                  return true;
                }

                return reactRenderer.ref?.onKeyDown(props) ?? false;
              },
              onStart: props => {
                setAtDropdownMenuIsOpen(true);

                if (!props.clientRect) return;

                reactRenderer = new ReactRenderer<
                  MentionListHandle,
                  MentionListProps
                >(MentionList, {
                  editor: props.editor,
                  props
                });

                reactRenderer.element.style.position = 'absolute';

                document.body.appendChild(reactRenderer.element);

                void updatePosition(props.editor, reactRenderer.element);
              },
              onUpdate(props) {
                reactRenderer.updateProps(props);

                if (!props.clientRect) return;

                void updatePosition(props.editor, reactRenderer.element);
              }
            };
          }
        }
      }),
      DateField,
      ComboBoxField,
      NumberField,
      TextField,
      SelectField
    ]
  });

  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => editor.getText({ textSerializers })
  });

  let submitButtonIsDisabled = editorState.trim() === '';

  editor.state.doc.descendants(node => {
    if (
      (node.type.name === 'comboBoxField' &&
        (node.attrs as ComboBoxAttributes).value === null) ||
      (node.type.name === 'dateField' &&
        (node.attrs as DateFieldAttributes).value === undefined) ||
      (node.type.name === 'mention' &&
        (node.attrs as MentionAttributes).label.endsWith(': Custom')) ||
      (node.type.name === 'numberField' &&
        (node.attrs as NumberFieldAttributes).value === '') ||
      (node.type.name === 'selectField' &&
        (node.attrs as SelectAttributes).value === null) ||
      (node.type.name === 'tag' &&
        (node.attrs as TagAttributes).text.endsWith(': Custom')) ||
      (node.type.name === 'textField' &&
        (node.attrs as TextFieldAttributes).value === '')
    ) {
      submitButtonIsDisabled = true;
      return false;
    }
  });

  useEffect(() => {
    if (editorState === '') {
      setSelectedPreset(null);
    }
  }, [editorState]);

  useEffect(() => {
    if (state === 'read-only') {
      const { tr } = editor.state;

      editor.state.doc.descendants((node, pos) => {
        if (
          node.type.name === 'comboBoxField' ||
          node.type.name === 'dateField' ||
          node.type.name === 'numberField' ||
          node.type.name === 'selectField' ||
          node.type.name === 'textField'
        ) {
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, isReadOnly: true });
        }
      });

      editor.view.dispatch(tr);
    }
  }, [editor.state, editor.view, state]);

  const handleDragEnter: DOMAttributes<HTMLDivElement>['onDragEnter'] =
    event => {
      event.preventDefault();

      dragDepthRef.current += 1;
      setIsDraggingOver(true);
    };

  const handleDragLeave: DOMAttributes<HTMLDivElement>['onDragLeave'] = () => {
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);

    if (dragDepthRef.current === 0) {
      setIsDraggingOver(false);
    }
  };

  const handleDragOver: DOMAttributes<HTMLDivElement>['onDragOver'] = event => {
    event.preventDefault();
  };

  const handleDrop: DOMAttributes<HTMLDivElement>['onDrop'] = event => {
    event.preventDefault();

    dragDepthRef.current = 0;
    setIsDraggingOver(false);
  };

  const handleStopButtonClick: DOMAttributes<HTMLButtonElement>['onClick'] =
    useCallback(() => {
      if (state === 'read-only') return;

      remainingProps.setPending(false);

      if (!remainingProps.socket) {
        throw new Error();
      }

      void (async () => {
        const authData = await getJWT();

        if (!authData) {
          return;
        }

        // @ts-expect-error: fix this later.
        remainingProps.socket.send(
          JSON.stringify({
            data: {
              access_token: authData.accessToken,
              session_id: remainingProps.sessionId,
              user_id: authData.userId
            },
            message_id: remainingProps.messageId,
            type: 'stop'
          })
        );

        remainingProps.setPending(false);

        remainingProps.setPromptsAndAnswers(promptsAndAnswers =>
          produce(promptsAndAnswers, draft => {
            draft[draft.length - 1].thinkingDuration = formatDuration(
              intervalToDuration({
                end: formatISO(new Date()),
                start: draft[draft.length - 1].startTimestamp
              }),
              {
                delimiter: ' and ',
                format: ['minutes', 'seconds']
              }
            );

            draft[draft.length - 1].tasks = draft[
              draft.length - 1
            ].tasks.filter(task => task.type !== 'input-fields');
          })
        );
      })();
    }, [remainingProps, state, getJWT]);

  const handleSubmitButtonClick: DOMAttributes<HTMLButtonElement>['onClick'] =
    () => {
      if (state === 'read-only') return;

      if (state === 'editing') {
        remainingProps.setIsEditing(false);
      }

      void sendMessage(editor.getText({ textSerializers }), editor.getJSON());

      setTimeout(() => {
        editor.commands.clearContent();
      }, 0);

      if (state === 'prompting') {
        setTimeout(() => {
          window.scrollTo({
            behavior: 'smooth',
            top: document.body.scrollHeight
          });
        }, 100);
      }
    };

  const handleXButtonClick: DOMAttributes<HTMLButtonElement>['onClick'] =
    useCallback(() => {
      if (state !== 'editing') return;

      remainingProps.setIsEditing(false);
    }, [remainingProps, state]);

  const providerValue = useMemo(() => ({ editor }), [editor]);

  type CommandItemOnSelect = NonNullable<
    ComponentProps<typeof CommandItem>['onSelect']
  >;

  const handleCommandItemSelect = useCallback<
    (businessEntity: BusinessEntity) => CommandItemOnSelect
  >(
    businessEntity => value => {
      editor
        .chain()
        .focus()
        .insertTag(value, businessEntity.color, businessEntity.iconName)
        .run();

      setAddDropdownIsOpen(false);
    },
    [editor, setAddDropdownIsOpen]
  );

  type DropdownMenuItemOnSelect = NonNullable<
    ComponentProps<typeof DropdownMenuItem>['onSelect']
  >;

  const handleDropdownMenuItemSelect = useCallback<
    (
      text: string,
      color: string,
      iconName: keyof typeof icons
    ) => DropdownMenuItemOnSelect
  >(
    (text, color, iconName) => () => {
      editor.chain().focus().insertTag(text, color, iconName).run();
    },
    [editor]
  );

  const handlePresetSelect = useCallback<CommandItemOnSelect>(
    value => {
      if (state === 'read-only') return;

      const selectedPreset = remainingProps.presetPrompts.find(
        presetPrompt => presetPrompt.label === value
      );

      if (!selectedPreset) {
        throw new Error();
      }

      const parts = selectedPreset.prompt
        .split(/(\{.*?\})/)
        .filter(part => part !== '');

      setSelectedPreset(selectedPreset);

      setPopoverIsOpen(false);

      const content: JSONContent = {
        content: [
          {
            content: parts.map(part => {
              if (part.startsWith('{') && part.endsWith('}')) {
                const variable = selectedPreset.variables.find(
                  variable => variable.name === part.slice(1, -1)
                );

                if (!variable) return {};

                if (variable.type === 'combo-box') {
                  const businessEntity = (
                    Array.isArray(remainingProps.businessEntities)
                      ? remainingProps.businessEntities
                      : []
                  ).find(businessEntity => businessEntity.id === variable.name);

                  if (!businessEntity) {
                    throw new Error();
                  }

                  return {
                    attrs: {
                      id: variable.name,
                      options: businessEntity.items.map(item => ({
                        label: item.name,
                        value: item.name
                      })),
                      placeholder: variable.placeholder,
                      value: variable.default_value
                    },
                    type: 'comboBoxField'
                  };
                } else if (variable.type === 'date') {
                  return {
                    attrs: {
                      id: variable.name,
                      placeholder: variable.placeholder,
                      value: variable.default_value ?? undefined
                    },
                    type: 'dateField'
                  };
                } else if (variable.type === 'number') {
                  return {
                    attrs: {
                      id: variable.name,
                      placeholder: variable.placeholder,
                      value: variable.default_value
                    },
                    type: 'numberField'
                  };
                } else if (variable.type === 'select') {
                  const businessEntity = businessEntities.find(
                    businessEntity => businessEntity.id === variable.name
                  );

                  if (!businessEntity) {
                    throw new Error();
                  }

                  return {
                    attrs: {
                      id: variable.name,
                      options: businessEntity.items.map(item => ({
                        label: item.name,
                        value: item.name
                      })),
                      placeholder: variable.placeholder,
                      value: variable.default_value
                    },
                    type: 'selectField'
                  };
                } else {
                  return {
                    attrs: {
                      id: variable.name,
                      placeholder: variable.placeholder,
                      value: variable.default_value ?? ''
                    },
                    type: 'textField'
                  };
                }
              } else {
                return {
                  text: part,
                  type: 'text'
                };
              }
            }),
            type: 'paragraph'
          }
        ],
        type: 'doc'
      };

      editor.chain().setContent(content).focus().run();
    },
    [
      editor,
      remainingProps,
      state,
      businessEntities,
      setSelectedPreset,
      setPopoverIsOpen
    ]
  );

  useEffect(() => {
    if (state === 'read-only') return;

    handlePresetSelectRef.current = handlePresetSelect;
  }, [handlePresetSelect, handlePresetSelectRef, state]);

  const handleRemovePresetButtonClick = useCallback<
    NonNullable<DOMAttributes<HTMLButtonElement>['onClick']>
  >(() => {
    setSelectedPreset(null);
    editor.chain().clearContent().focus().run();
  }, [editor, setSelectedPreset]);

  // Fetch user sources
  const fetchUserSources = useCallback(async () => {
    if (state === 'read-only') return;

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/sources/user`,
        {
          credentials: 'include',
          method: 'GET'
        }
      );

      if (response.ok) {
        const data = (await response.json()) as SourceUserResponse;
        if (data.success && data.data) {
          setUserSources(data.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch sources:', error);
    } finally {
      setIsLoadingSources(false);
    }
  }, [state]);

  // Fetch user sources on mount
  useEffect(() => {
    void fetchUserSources();
  }, [fetchUserSources]);

  // Listen for OAuth completion messages
  useEffect(() => {
    console.log('[PromptField] Setting up OAuth message listener');
    
    const handleMessage = async (event: MessageEvent) => {
      console.log('[PromptField] Received message:', event.data, 'from origin:', event.origin);
      
      // Verify origin for security
      if (event.origin !== window.location.origin) {
        console.log('[PromptField] Message origin mismatch, ignoring');
        return;
      }

      if (event.data?.type === 'oauth_success') {
        console.log('[PromptField] OAuth success message received!');
        setIsOAuthInProgress(false);

        // Clear stored OAuth state
        sessionStorage.removeItem('omni_oauth_state');

        // Activate the source that was pending OAuth
        const sourceToActivate = pendingOAuthSourceRef.current;
        console.log('[OAuth Success] Source to activate:', sourceToActivate);
        console.log('[OAuth Success] Current sources:', userSources);

        if (sourceToActivate) {
          // Optimistically update UI state immediately
          setUserSources(prev => {
            const updated = prev.map(s => {
              const sourceName = s.source?.name;
              if (sourceName === sourceToActivate) {
                console.log('[OAuth Success] Found matching source:', sourceName, 'updating to active');
                return { ...s, is_active: true };
              }
              return s;
            });
            console.log('[OAuth Success] Updated sources after optimistic update:', updated);
            return updated;
          });

          // Make API call to activate the source (now that we have valid API key)
          void (async () => {
            try {
              console.log('[OAuth Success] Making API call to activate:', sourceToActivate);
              const response = await fetch(
                `${import.meta.env.VITE_API_BASE_URL}/sources/user/${sourceToActivate}/toggle`,
                {
                  body: JSON.stringify({ is_active: true }),
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  method: 'PUT'
                }
              );

              console.log('[OAuth Success] API response status:', response.status);
              const data = (await response.json()) as SourceToggleResponse;
              console.log('[OAuth Success] API response data:', data);

              if (response.ok && data.success && data.data) {
                // Update state with server response
                console.log('[OAuth Success] Server response data:', data.data);
                setUserSources(prev => {
                  const updated = prev.map(s => {
                    if (s.source?.name === sourceToActivate) {
                      console.log('[OAuth Success] Updating source with server data:', s.source.name);
                      return data.data!;
                    }
                    return s;
                  });
                  console.log('[OAuth Success] Final updated sources:', updated);
                  return updated;
                });
                toast.success(
                  `${sourceToActivate === 'backend' ? 'Omni' : sourceToActivate} source activated successfully!`
                );
              } else {
                console.error('[OAuth Success] Activation failed:', data);
                // Try refetching sources as fallback - backend might have activated it
                await fetchUserSources();
                toast.error(
                  'OAuth successful, but activation response was unexpected. Sources refreshed.'
                );
              }
            } catch (error) {
              console.error(
                `[OAuth Success] Failed to activate ${sourceToActivate} source after OAuth:`,
                error
              );
              // Revert optimistic update on error
              setUserSources(prev =>
                prev.map(s =>
                  s.source?.name === sourceToActivate
                    ? { ...s, is_active: false }
                    : s
                )
              );
              toast.error(
                'OAuth successful, but failed to activate source. Please try toggling it manually.'
              );
            } finally {
              // Clear the ref after API call completes
              pendingOAuthSourceRef.current = null;
            }
          })();
        } else {
          console.warn('[OAuth Success] No pending source to activate');
        }

        // Close popup if still open
        if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
          oauthPopupRef.current.close();
        }
        oauthPopupRef.current = null;
      } else if (event.data?.type === 'oauth_error') {
        setIsOAuthInProgress(false);
        if (oauthPopupRef.current) {
          oauthPopupRef.current.close();
          oauthPopupRef.current = null;
        }
        sessionStorage.removeItem('omni_oauth_state');
        pendingOAuthSourceRef.current = null;
        const errorMessage =
          event.data.error || 'OAuth authentication failed. Please try again.';
        toast.error(errorMessage);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [fetchUserSources]);

  const handleSourceToggle = useCallback(
    async (sourceName: string, isActive: boolean) => {
      // Set loading state
      setTogglingSourceName(sourceName);

      // Optimistic update - update UI immediately
      setUserSources(prev =>
        prev.map(s =>
          s.source?.name === sourceName ? { ...s, is_active: isActive } : s
        )
      );

      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/sources/user/${sourceName}/toggle`,
          {
            body: JSON.stringify({ is_active: isActive }),
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            },
            method: 'PUT'
          }
        );

        if (response.ok) {
          const data = (await response.json()) as SourceToggleResponse;

          // Check if OAuth is required
          if (data.requires_oauth && (sourceName === 'backend' || sourceName === 'opstream') && isActive) {
            // Store which source we're trying to activate
            pendingOAuthSourceRef.current = sourceName;

            // Revert optimistic update
            setUserSources(prev =>
              prev.map(s =>
                s.source?.name === sourceName
                  ? { ...s, is_active: !isActive }
                  : s
              )
            );

            // Open popup immediately (must be in user interaction context)
            // We'll navigate it to the authorization URL once we get it
            const popup = window.open(
              'about:blank',
              'omni_oauth',
              'width=600,height=700,scrollbars=yes,resizable=yes'
            );

            if (!popup) {
              // Popup blocked - show error and clean up
              toast.error(
                'Popup blocked. Please allow popups for this site and try again.',
                {
                  duration: 5000
                }
              );
              setTogglingSourceName(null);
              pendingOAuthSourceRef.current = null;
              return;
            }

            oauthPopupRef.current = popup;
            setIsOAuthInProgress(true);

            // Initiate OAuth flow
            try {
              const callbackUrl = `${window.location.origin}/oauth/omni/callback`;
              const initiateResponse = await fetch(
                `${import.meta.env.VITE_API_BASE_URL}/oauth/omni/initiate`,
                {
                  body: JSON.stringify({
                    frontend_callback_url: callbackUrl
                  } as OmniOAuthInitiateRequest),
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  method: 'POST'
                }
              );

              if (initiateResponse.ok) {
                const initiateData =
                  (await initiateResponse.json()) as OmniOAuthInitiateResponse;

                if (initiateData.success && initiateData.authorization_url) {
                  // Store state in sessionStorage
                  sessionStorage.setItem('omni_oauth_state', initiateData.state);

                  // Navigate the popup to the authorization URL
                  if (popup && !popup.closed) {
                    popup.location.href = initiateData.authorization_url;
                  } else {
                    // Popup was closed before we could navigate
                    setIsOAuthInProgress(false);
                    oauthPopupRef.current = null;
                    sessionStorage.removeItem('omni_oauth_state');
                    pendingOAuthSourceRef.current = null;
                    setTogglingSourceName(null);
                    toast.error('OAuth popup was closed. Please try again.');
                    return;
                  }

                  // Check if popup is closed manually
                  const checkPopupClosed = setInterval(() => {
                    if (popup.closed) {
                      clearInterval(checkPopupClosed);
                      setIsOAuthInProgress(false);
                      oauthPopupRef.current = null;
                      sessionStorage.removeItem('omni_oauth_state');
                      pendingOAuthSourceRef.current = null;
                    }
                  }, 1000);
                } else {
                  throw new Error(
                    initiateData.error ||
                      initiateData.message ||
                      'Failed to initiate OAuth flow'
                  );
                }
              } else {
                const errorData = (await initiateResponse.json()) as {
                  error?: string;
                  message?: string;
                };
                throw new Error(
                  errorData.error || errorData.message || 'Failed to initiate OAuth'
                );
              }
            } catch (error) {
              // Close popup if it was opened
              if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
                oauthPopupRef.current.close();
              }
              oauthPopupRef.current = null;
              setIsOAuthInProgress(false);
              sessionStorage.removeItem('omni_oauth_state');
              pendingOAuthSourceRef.current = null;

              const errorMessage =
                error instanceof Error
                  ? error.message
                  : 'Failed to start OAuth authentication. Please try again.';
              toast.error(errorMessage);
              setTogglingSourceName(null);
            }
            return;
          }

          // Normal toggle response handling
          if (data.success && data.data) {
            // Update with server response
            setUserSources(prev =>
              prev.map(s => (s.source?.name === sourceName ? data.data! : s))
            );
          } else {
            // Revert on unsuccessful response
            setUserSources(prev =>
              prev.map(s =>
                s.source?.name === sourceName
                  ? { ...s, is_active: !isActive }
                  : s
              )
            );
          }
        } else {
          // Revert on HTTP error
          setUserSources(prev =>
            prev.map(s =>
              s.source?.name === sourceName ? { ...s, is_active: !isActive } : s
            )
          );
        }
      } catch (error) {
        console.error('Failed to toggle source:', error);
        // Revert on exception
        setUserSources(prev =>
          prev.map(s =>
            s.source?.name === sourceName ? { ...s, is_active: !isActive } : s
          )
        );
      } finally {
        setTogglingSourceName(null);
      }
    },
    []
  );

  if (state === 'read-only') {
    return (
      <EditorContext value={providerValue}>
        <EditorContent editor={editor} />
      </EditorContext>
    );
  }

  return (
    <div
      className="relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div
        className={cn(
          'bg-card relative rounded-xl border backdrop-blur-[50px] transition-colors',
          isDraggingOver && 'border-dashed opacity-30',
          state === 'editing' && 'mb-4 shadow-2xl'
        )}
      >
        <div
          className="relative"
          ref={containerRef}
        >
          {selectedPreset && (
            <AccordionPrimitive.Root
              collapsible
              data-slot="accordion"
              type="single"
            >
              <AccordionPrimitive.Item
                className="m-1.5 rounded-md bg-zinc-300 bg-linear-[var(--alpha-30)] px-1 py-0.5 dark:bg-zinc-500"
                data-slot="accordion-item"
                value="details"
              >
                <div className="flex justify-between">
                  <div className="flex items-center">
                    <div className="mr-1.5">{selectedPreset.icon}</div>
                    <div className="mr-0.75 text-sm font-medium">
                      {selectedPreset.label}
                    </div>
                    {selectedPreset.badge === 'powerpoint' ? (
                      <div className="flex gap-x-2.5 rounded-xl bg-orange-500 bg-linear-[var(--alpha-30)] py-0.5 pr-3 pl-1">
                        <img
                          className="size-5"
                          src="/powerpoint.svg"
                        />
                        <div className="text-sm text-orange-500">
                          PowerPoint
                        </div>
                      </div>
                    ) : selectedPreset.badge === 'word' ? (
                      <div className="flex gap-x-2.5 rounded-xl bg-blue-500 bg-linear-[var(--alpha-30)] py-0.5 pr-3 pl-1">
                        <img
                          className="size-5"
                          src="/word.svg"
                        />
                        <div className="text-sm text-blue-500">Word</div>
                      </div>
                    ) : null}
                  </div>
                  <div className="text-muted-foreground flex items-center">
                    <AccordionPrimitive.Header>
                      <AccordionPrimitive.Trigger
                        asChild
                        className="group"
                        data-slot="accordion-trigger"
                      >
                        <Button
                          size="sm"
                          variant="ghost"
                        >
                          Show Details
                          <ChevronDownIcon
                            aria-hidden
                            className="transition-transform group-data-[state=open]:rotate-180"
                            size={16}
                          />
                        </Button>
                      </AccordionPrimitive.Trigger>
                    </AccordionPrimitive.Header>
                    <Button
                      aria-label="Remove preset"
                      onClick={handleRemovePresetButtonClick}
                      size="icon"
                      variant="ghost"
                    >
                      <XIcon />
                    </Button>
                  </div>
                </div>
                <AccordionPrimitive.Content
                  className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down text-muted-foreground max-h-52 overflow-y-auto p-1"
                  data-slot="accordion-content"
                >
                  <div className="prose dark:prose-invert prose-sm prose-zinc max-w-none">
                    <Markdown>{selectedPreset.details}</Markdown>
                  </div>
                </AccordionPrimitive.Content>
              </AccordionPrimitive.Item>
            </AccordionPrimitive.Root>
          )}
          <FileAttachmentList
            files={fileAttachments}
            onRemove={id => {
              const attachment = fileAttachments.find(f => f.id === id);
              if (attachment?.fileId) {
                // Fire and forget - delete file asynchronously
                void (async () => {
                  try {
                    const auth = await getJWT();
                    if (!auth) {
                      throw new Error('Authentication required');
                    }
                    if (!attachment.fileId) {
                      throw new Error('File ID is required');
                    }
                    const response = await fetch(
                      `${import.meta.env.VITE_API_BASE_URL}/files/${attachment.fileId}`,
                      {
                        headers: {
                          Authorization: `Bearer ${auth.accessToken}`
                        },
                        method: 'DELETE'
                      }
                    );

                    if (!response.ok) {
                      const error = await response
                        .json()
                        .catch(() => ({ detail: response.statusText }));
                      throw new Error(
                        error?.detail ??
                          `Failed to delete file: ${response.status}`
                      );
                    }
                  } catch (error) {
                    console.error('Failed to delete file:', error);
                  }
                })();
              }
              setFileAttachments(prev => prev.filter(f => f.id !== id));
            }}
          />
          <div className="relative">
            <EditorContext value={providerValue}>
              <EditorContent editor={editor} />
            </EditorContext>
            {editorState === '' && (
              <div className="text-muted-foreground pointer-events-none absolute top-4 right-4 opacity-40 select-none">
                Type @ to mention or add items
              </div>
            )}
          </div>
          <input
            accept=".txt,.md,.pdf,.docx,.csv,.json"
            className="hidden"
            multiple
            onChange={e => {
              // Fire and forget - handle file upload asynchronously
              void (async () => {
                if (
                  e.target.files &&
                  e.target.files.length > 0 &&
                  state !== 'read-only'
                ) {
                  const sessionId =
                    'sessionId' in remainingProps
                      ? remainingProps.sessionId
                      : null;
                  if (!sessionId) {
                    const { toast } = await import('sonner');
                    toast.error('Session ID is required for file upload');
                    return;
                  }

                  const { toast } = await import('sonner');

                  // Get auth token
                  const auth = await getJWT();
                  if (!auth) {
                    toast.error('Authentication required for file upload');
                    return;
                  }

                  const filesArray = Array.from(e.target.files);

                  // Add files to state immediately
                  const newAttachments: FileAttachment[] = filesArray.map(
                    file => ({
                      file,
                      id: uuidv4(),
                      progress: 0,
                      status: 'uploading'
                    })
                  );
                  setFileAttachments(prev => [...prev, ...newAttachments]);

                  // Upload each file
                  for (let i = 0; i < filesArray.length; i++) {
                    const file = filesArray[i];
                    const attachment = newAttachments[i];

                    try {
                      // Update status: uploading
                      setFileAttachments(prev =>
                        prev.map(item =>
                          item.id === attachment.id
                            ? { ...item, progress: 10, status: 'uploading' }
                            : item
                        )
                      );

                      // Initialize upload
                      const initResponse = await fetch(
                        `${import.meta.env.VITE_API_BASE_URL}/files/init-upload`,
                        {
                          body: JSON.stringify({
                            content_type: file.type || undefined,
                            filename: file.name,
                            session_id: sessionId,
                            size_bytes: file.size
                          }),
                          headers: {
                            Authorization: `Bearer ${auth.accessToken}`,
                            'Content-Type': 'application/json'
                          },
                          method: 'POST'
                        }
                      );

                      if (!initResponse.ok) {
                        const error = await initResponse
                          .json()
                          .catch(() => ({ detail: initResponse.statusText }));
                        throw new Error(
                          error.detail ||
                            `Failed to init upload: ${initResponse.status}`
                        );
                      }

                      const initData = await initResponse.json();
                      const fileId = initData.file_id;

                      // Update with fileId
                      setFileAttachments(prev =>
                        prev.map(item =>
                          item.id === attachment.id
                            ? { ...item, fileId, progress: 25 }
                            : item
                        )
                      );

                      // Upload file to Azure Blob Storage
                      if (!initData.upload?.url) {
                        throw new Error(
                          'Failed to get upload URL from server. Azure Blob Storage may not be configured.'
                        );
                      }

                      setFileAttachments(prev =>
                        prev.map(item =>
                          item.id === attachment.id
                            ? { ...item, progress: 50 }
                            : item
                        )
                      );

                      // Upload to Azure Blob Storage using SAS URL
                      const azureResponse = await fetch(initData.upload.url, {
                        body: file,
                        headers: {
                          'Content-Type':
                            file.type || 'application/octet-stream',
                          'x-ms-blob-type': 'BlockBlob',
                          'x-ms-version': '2021-04-10'
                        },
                        method: 'PUT'
                      });

                      if (!azureResponse.ok) {
                        const errorText = await azureResponse
                          .text()
                          .catch(() => azureResponse.statusText);
                        throw new Error(
                          `Failed to upload to Azure: ${azureResponse.status} ${errorText}`
                        );
                      }

                      setFileAttachments(prev =>
                        prev.map(item =>
                          item.id === attachment.id
                            ? { ...item, progress: 75 }
                            : item
                        )
                      );

                      // Complete upload
                      const completeResponse = await fetch(
                        `${import.meta.env.VITE_API_BASE_URL}/files/${fileId}/complete`,
                        {
                          body: JSON.stringify({
                            file_id: fileId
                          }),
                          headers: {
                            Authorization: `Bearer ${auth.accessToken}`,
                            'Content-Type': 'application/json'
                          },
                          method: 'POST'
                        }
                      );

                      if (!completeResponse.ok) {
                        const error = await completeResponse
                          .json()
                          .catch(() => ({
                            detail: completeResponse.statusText
                          }));
                        throw new Error(
                          error.detail ||
                            `Failed to complete upload: ${completeResponse.status}`
                        );
                      }

                      // Update status: processing
                      setFileAttachments(prev =>
                        prev.map(item =>
                          item.id === attachment.id
                            ? { ...item, progress: 80, status: 'processing' }
                            : item
                        )
                      );

                      // Poll status
                      const terminalStates = ['ready', 'failed', 'blocked'];
                      let finalStatus: {
                        created_at?: string;
                        error_message?: null | string;
                        extension: string;
                        file_id: string;
                        original_filename: string;
                        processed_at?: string;
                        scan_result?: 'blocked' | 'clean' | null;
                        session_id: string;
                        size_bytes: number;
                        status:
                          | 'blocked'
                          | 'clean'
                          | 'failed'
                          | 'initiated'
                          | 'ready'
                          | 'uploaded';
                        updated_at?: string;
                        user_id: string;
                      } | null = null;

                      for (let attempt = 0; attempt < 60; attempt++) {
                        const statusResponse = await fetch(
                          `${import.meta.env.VITE_API_BASE_URL}/files/${fileId}/status`,
                          {
                            headers: {
                              Authorization: `Bearer ${auth.accessToken}`
                            },
                            method: 'GET'
                          }
                        );

                        if (!statusResponse.ok) {
                          const error = await statusResponse
                            .json()
                            .catch(() => ({
                              detail: statusResponse.statusText
                            }));
                          throw new Error(
                            error.detail ||
                              `Failed to get file status: ${statusResponse.status}`
                          );
                        }

                        finalStatus = await statusResponse.json();

                        setFileAttachments(prev =>
                          prev.map(item =>
                            item.id === attachment.id
                              ? { ...item, fileId: finalStatus!.file_id }
                              : item
                          )
                        );

                        if (
                          finalStatus &&
                          terminalStates.includes(finalStatus.status)
                        ) {
                          break;
                        }

                        // Wait before next poll
                        await new Promise(resolve => setTimeout(resolve, 1000));
                      }

                      if (
                        !finalStatus ||
                        !terminalStates.includes(finalStatus.status)
                      ) {
                        throw new Error(
                          `File processing timeout. Status: ${finalStatus?.status || 'unknown'}. Max attempts (60) reached.`
                        );
                      }

                      // Update final status
                      const finalStatusValue =
                        finalStatus.status === 'ready'
                          ? 'ready'
                          : finalStatus.status === 'blocked'
                            ? 'blocked'
                            : 'failed';

                      setFileAttachments(prev =>
                        prev.map(item =>
                          item.id === attachment.id
                            ? {
                                ...item,
                                error: finalStatus.error_message || undefined,
                                progress: 100,
                                status: finalStatusValue
                              }
                            : item
                        )
                      );

                      if (finalStatusValue === 'ready') {
                        const attachResponse = await fetch(
                          `${import.meta.env.VITE_API_BASE_URL}/sessions/${sessionId}/files/${fileId}/attach`,
                          {
                            headers: {
                              Authorization: `Bearer ${auth.accessToken}`
                            },
                            method: 'POST'
                          }
                        );

                        if (!attachResponse.ok) {
                          const error = await attachResponse
                            .json()
                            .catch(() => ({
                              detail: attachResponse.statusText
                            }));
                          throw new Error(
                            error.detail ||
                              `Failed to attach file: ${attachResponse.status}`
                          );
                        }

                        toast.success(
                          `File "${file.name}" uploaded successfully`
                        );
                      } else if (finalStatusValue === 'blocked') {
                        toast.error(
                          `File "${file.name}" was blocked by security scan`
                        );
                      } else {
                        toast.error(`File "${file.name}" processing failed`);
                      }
                    } catch (error) {
                      const errorMessage =
                        error instanceof Error
                          ? error.message
                          : 'Upload failed';
                      setFileAttachments(prev =>
                        prev.map(item =>
                          item.id === attachment.id
                            ? { ...item, error: errorMessage, status: 'failed' }
                            : item
                        )
                      );
                      toast.error(`Failed to upload "${file.name}"`, {
                        description: errorMessage
                      });
                    }
                  }

                  // Reset input
                  e.target.value = '';
                }
              })();
            }}
            ref={fileInputRef}
            type="file"
          />
        </div>
        <div className="flex justify-between p-2">
          <div className="flex">
            <DropdownMenuPrimitive.Root
              data-slot="dropdown-menu"
              onOpenChange={setAddDropdownIsOpen}
              open={addDropdownIsOpen}
            >
              <DropdownMenuPrimitive.Trigger
                asChild
                data-slot="dropdown-menu-trigger"
              >
                <Button variant="ghost">
                  <AtSignIcon />
                  Add
                </Button>
              </DropdownMenuPrimitive.Trigger>
              <DropdownMenuPrimitive.Portal>
                <DropdownMenuPrimitive.Content
                  align="start"
                  className="bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border p-1 shadow-md"
                  data-slot="dropdown-menu-content"
                  onCloseAutoFocus={event => {
                    event.preventDefault();
                  }}
                  side="top"
                  sideOffset={4}
                >
                  {businessEntities.map(businessEntity => {
                    // eslint-disable-next-line import/namespace
                    const Icon = icons[businessEntity.iconName];

                    const textColor = {
                      amber: 'text-amber-500',
                      blue: 'text-blue-500',
                      cyan: 'text-cyan-500',
                      emerald: 'text-emerald-500',
                      fuchsia: 'text-fuchsia-500',
                      green: 'text-green-500',
                      indigo: 'text-indigo-500',
                      lime: 'text-lime-500',
                      orange: 'text-orange-500',
                      pink: 'text-pink-500',
                      purple: 'text-purple-500',
                      red: 'text-red-500',
                      rose: 'text-rose-500',
                      sky: 'text-sky-500',
                      teal: 'text-teal-500',
                      violet: 'text-violet-500',
                      yellow: 'text-yellow-500'
                    }[businessEntity.color];

                    const backgroundColorOnFocus = {
                      amber: 'focus:bg-amber-500',
                      blue: 'focus:bg-blue-500',
                      cyan: 'focus:bg-cyan-500',
                      emerald: 'focus:bg-emerald-500',
                      fuchsia: 'focus:bg-fuchsia-500',
                      green: 'focus:bg-green-500',
                      indigo: 'focus:bg-indigo-500',
                      lime: 'focus:bg-lime-500',
                      orange: 'focus:bg-orange-500',
                      pink: 'focus:bg-pink-500',
                      purple: 'focus:bg-purple-500',
                      red: 'focus:bg-red-500',
                      rose: 'focus:bg-rose-500',
                      sky: 'focus:bg-sky-500',
                      teal: 'focus:bg-teal-500',
                      violet: 'focus:bg-violet-500',
                      yellow: 'focus:bg-yellow-500'
                    }[businessEntity.color];

                    const backgroundColorOnOpen = {
                      amber: 'data-[state=open]:bg-amber-500',
                      blue: 'data-[state=open]:bg-blue-500',
                      cyan: 'data-[state=open]:bg-cyan-500',
                      emerald: 'data-[state=open]:bg-emerald-500',
                      fuchsia: 'data-[state=open]:bg-fuchsia-500',
                      green: 'data-[state=open]:bg-green-500',
                      indigo: 'data-[state=open]:bg-indigo-500',
                      lime: 'data-[state=open]:bg-lime-500',
                      orange: 'data-[state=open]:bg-orange-500',
                      pink: 'data-[state=open]:bg-pink-500',
                      purple: 'data-[state=open]:bg-purple-500',
                      red: 'data-[state=open]:bg-red-500',
                      rose: 'data-[state=open]:bg-rose-500',
                      sky: 'data-[state=open]:bg-sky-500',
                      teal: 'data-[state=open]:bg-teal-500',
                      violet: 'data-[state=open]:bg-violet-500',
                      yellow: 'data-[state=open]:bg-yellow-500'
                    }[businessEntity.color];

                    if (
                      !textColor ||
                      !backgroundColorOnOpen ||
                      !backgroundColorOnFocus
                    ) {
                      throw new Error();
                    }

                    return (
                      <DropdownMenuSub key={businessEntity.id}>
                        <DropdownMenuSubTrigger
                          className={`${backgroundColorOnFocus} focus:bg-linear-[var(--alpha-30)] ${backgroundColorOnOpen} data-[state=open]:bg-linear-[var(--alpha-30)]`}
                        >
                          <Icon
                            className={`mr-2 ${textColor}`}
                            size={16}
                          />
                          {businessEntity.name}
                        </DropdownMenuSubTrigger>
                        {businessEntity.items.some(item => item.items) ? (
                          <DropdownMenuSubContent>
                            {businessEntity.items.map(item =>
                              item.items ? (
                                <DropdownMenuSub key={item.id}>
                                  <DropdownMenuSubTrigger>
                                    {item.name}
                                    <ChevronRightIcon className="ml-auto size-4" />
                                  </DropdownMenuSubTrigger>
                                  <DropdownMenuSubContent>
                                    {item.items.map(subItem => (
                                      <DropdownMenuItem
                                        key={subItem.id}
                                        onSelect={handleDropdownMenuItemSelect(
                                          `${item.name}: ${subItem.name}`,
                                          businessEntity.color,
                                          businessEntity.iconName
                                        )}
                                      >
                                        {subItem.name}
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuSubContent>
                                </DropdownMenuSub>
                              ) : (
                                <DropdownMenuItem
                                  key={item.id}
                                  onSelect={handleDropdownMenuItemSelect(
                                    item.name,
                                    businessEntity.color,
                                    businessEntity.iconName
                                  )}
                                >
                                  {item.name}
                                </DropdownMenuItem>
                              )
                            )}
                          </DropdownMenuSubContent>
                        ) : (
                          <DropdownMenuSubContent>
                            <Command>
                              <CommandInput
                                autoFocus
                                className="h-9"
                                placeholder="Search"
                              />
                              <CommandList>
                                <CommandEmpty>No results.</CommandEmpty>
                                <CommandGroup>
                                  {businessEntity.items.map(item => (
                                    <CommandItem
                                      key={item.id}
                                      onSelect={handleCommandItemSelect(
                                        businessEntity
                                      )}
                                      value={item.name}
                                    >
                                      {item.name}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </DropdownMenuSubContent>
                        )}
                      </DropdownMenuSub>
                    );
                  })}
                  <DropdownMenuItem
                    onSelect={e => {
                      e.preventDefault();
                      // Trigger file input click
                      if (fileInputRef.current) {
                        fileInputRef.current.click();
                      }
                      setAddDropdownIsOpen(false);
                    }}
                  >
                    <PaperclipIcon className="size-4" />
                    Add files
                  </DropdownMenuItem>
                </DropdownMenuPrimitive.Content>
              </DropdownMenuPrimitive.Portal>
            </DropdownMenuPrimitive.Root>
            <Popover>
              <Tooltip>
                <PopoverTrigger asChild>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon"
                      variant="ghost"
                    >
                      <GlobeIcon />
                    </Button>
                  </TooltipTrigger>
                </PopoverTrigger>
                <TooltipContent>
                  <span>Sources</span>
                </TooltipContent>
              </Tooltip>
              <PopoverContent
                align="start"
                className="bg-popover w-fit space-y-1 rounded-xl p-1"
                side="top"
              >
                {isLoadingSources ? (
                  <div className="text-muted-foreground p-4 text-sm">
                    Loading sources...
                  </div>
                ) : userSources.length === 0 ? (
                  <div className="text-muted-foreground p-4 text-sm">
                    No sources available
                  </div>
                ) : (
                  userSources.map(userSource => {
                    const isToggling =
                      togglingSourceName === userSource.source?.name;
                    const isOAuthPending =
                      isOAuthInProgress &&
                      (userSource.source?.name === 'backend' || userSource.source?.name === 'opstream');
                    const isLoading = isToggling || isOAuthPending;
                    return (
                      <div
                        className={cn(
                          'flex items-center gap-x-3 rounded-xl px-2 py-1.5',
                          userSource.is_active && 'bg-background',
                          isLoading && 'opacity-70',
                          isLoading && 'cursor-wait'
                        )}
                        key={userSource.id}
                      >
                        {getSourceLogo(userSource.source?.name ?? '')}
                        <div>
                          <div className="text-sm font-medium">
                            {userSource.source?.title ??
                              userSource.source?.name}
                          </div>
                          {userSource.source?.description && (
                            <div className="text-muted-foreground w-80 text-sm">
                              {userSource.source.description}
                            </div>
                          )}
                        </div>
                        <Switch
                          checked={userSource.is_active}
                          className={cn(
                            'ml-auto',
                            isLoading ? 'cursor-wait' : 'cursor-pointer',
                            isLoading && 'animate-pulse'
                          )}
                          disabled={isLoading}
                          onCheckedChange={checked => {
                            void handleSourceToggle(
                              userSource.source?.name ?? '',
                              checked
                            );
                          }}
                        />
                      </div>
                    );
                  })
                )}
              </PopoverContent>
            </Popover>
            <Popover
              onOpenChange={setPopoverIsOpen}
              open={popoverIsOpen}
            >
              <Tooltip>
                <PopoverTrigger asChild>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon"
                      variant="ghost"
                    >
                      <ZapIcon />
                    </Button>
                  </TooltipTrigger>
                </PopoverTrigger>
                <TooltipContent>
                  <span>Presets</span>
                </TooltipContent>
              </Tooltip>
              <PopoverContent
                align="start"
                className="w-fit p-0"
                side="top"
              >
                <Command>
                  <CommandInput placeholder="Search" />
                  <CommandList>
                    <CommandEmpty>No results found.</CommandEmpty>
                    {[
                      {
                        commandItems: remainingProps.presetPrompts,
                        heading: 'All'
                      }
                    ].map(commandGroup => (
                      <CommandGroup
                        heading={commandGroup.heading}
                        key={commandGroup.heading}
                      >
                        {commandGroup.commandItems.map(commandItem => (
                          <CommandItem
                            key={commandItem.id}
                            onSelect={handlePresetSelect}
                            value={commandItem.label}
                          >
                            {commandItem.icon && <div>{commandItem.icon}</div>}
                            {commandItem.label}
                            {commandItem.badge === 'powerpoint' ? (
                              <div className="flex gap-x-2.5 rounded-xl bg-orange-500 bg-linear-[var(--alpha-30)] py-0.5 pr-3 pl-1">
                                <img
                                  className="size-5"
                                  src="/powerpoint.svg"
                                />
                                <div className="text-sm text-orange-500">
                                  PowerPoint
                                </div>
                              </div>
                            ) : commandItem.badge === 'word' ? (
                              <div className="flex gap-x-2.5 rounded-xl bg-blue-500 bg-linear-[var(--alpha-30)] py-0.5 pr-3 pl-1">
                                <img
                                  className="size-5"
                                  src="/word.svg"
                                />
                                <div className="text-sm text-blue-500">
                                  Word
                                </div>
                              </div>
                            ) : null}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    ))}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          {state === 'editing' ? (
            <div className="flex gap-x-2">
              <Button
                onClick={handleXButtonClick}
                size="icon"
                type="button"
                variant="outline"
              >
                <XIcon />
              </Button>
              <Button
                disabled={submitButtonIsDisabled}
                onClick={handleSubmitButtonClick}
                variant="brand"
              >
                <RefreshCwIcon />
                Update Query
              </Button>
            </div>
          ) : remainingProps.pending ? (
            <Button
              onClick={handleStopButtonClick}
              size="icon"
              variant="destructive"
            >
              <SquareIcon fill="currentColor" />
            </Button>
          ) : (
            <Button
              disabled={submitButtonIsDisabled}
              onClick={handleSubmitButtonClick}
              size="icon"
              variant="brand"
            >
              <ArrowUpIcon />
            </Button>
          )}
        </div>
      </div>
      {isDraggingOver && (
        <div className="bg-accent dark:bg-input/50 absolute inset-0 flex items-center justify-center rounded-xl">
          <div className="text-brand flex items-center gap-x-2">
            <FilePlusIcon size={20} />
            <p>Drop to upload file here</p>
          </div>
        </div>
      )}
    </div>
  );
};

export { Tag };

export default PromptField;
